import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToCatalogProducts } from '../utils/firebase';
import type { CatalogProduct } from '../utils/firebase';
import { bookData } from './pages/site/bookData';
function formatPrice(cents: number): string {
  const dollars = (cents / 100).toFixed(2);
  return "$" + dollars;
}
function getCartItemImage(item: {
  productId: string;
  name: string;
  type: string;
}, products: CatalogProduct[]): string | undefined {
  const name = item.name?.trim().toLowerCase();
  if (item.type === "book") {
    const b = products.find(p => p.id === item.productId) ?? products.find(p => p.name.trim().toLowerCase() === name);
    return b?.image;
  }
  const p = products.find(p => p.id === item.productId) ?? products.find(p => p.name.trim().toLowerCase() === name);
  return p?.image;
}
export function CartDrawer() {
  const {
    items,
    subtotal,
    itemCount,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity
  } = useCart();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = React.useState<CatalogProduct[]>([]);
  useEffect(() => {
    return subscribeToCatalogProducts(setProducts);
  }, []);
  const isShopOrBooks = location.pathname === '/shop' || location.pathname === '/performance' || location.pathname === '/gut-health';
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, closeCart]);
  const handleCheckout = () => {
    closeCart();
    navigate('/checkout-v2');
  };
  return <AnimatePresence data-id="element-58" data-mp="1fh3mmeobn4o7">
      {isOpen && isShopOrBooks && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-[60]" onClick={closeCart} aria-hidden="true" data-id="element-59" data-mp="1kfwrjipjlncr" />

          {/* Panel */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.3,
        ease: [0.2, 0.65, 0.3, 0.9]
      }} className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#F9F9F9] z-[70] shadow-2xl flex flex-col" role="dialog" aria-label="Shopping cart" data-id="element-60" data-mp="1z7fddx532c41">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E5E5] bg-white" data-id="element-61" data-mp="3epv7lrqd5mnb">
              <div className="flex items-center gap-3" data-id="element-62" data-mp="39r0kidld5bxe">
                <h2 className="text-lg font-bold text-[#111111] tracking-tight font-[Avenir,_sans-serif]" data-id="element-63" data-mp="b7tqerz0azkh">
                  Your Cart
                </h2>
                <span className="w-px h-5 bg-[#E5E5E5]" data-id="element-64" data-mp="69s6y5xo4y8h" />
                <span className="font-medium text-[#5A5A5A] text-[18px]" data-id="element-65" data-mp="1hibkadsb5yt">
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </span>
              </div>
              <button onClick={closeCart} className="p-2 text-[#9A9A90] hover:text-tealslate transition-colors" aria-label="Close cart" data-id="element-66" data-mp="tyb71y6ke4ph">
                <X className="w-5 h-5" data-id="element-67" data-mp="p72wuzxeufb9" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-6 bg-[#FFFFFF]" data-id="element-68" data-mp="9o9zmkjn1w18">
              {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-69" data-mp="dszt62qtqyiz">
                  <div className="w-16 h-16 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mb-5" data-id="element-70" data-mp="3vymaqono96q1">
                    <ShoppingBag className="w-7 h-7 text-[#9A9A90]" data-id="element-71" data-mp="sgcim2jr5oen" />
                  </div>
                  <h3 className="text-xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-72" data-mp="sip06elllwr6">
                    Your cart is empty.
                  </h3>
                  <p className="text-sm font-medium text-[#5A5A5A] mb-8 max-w-xs leading-relaxed" data-id="element-73" data-mp="ipht8pb7m8oz">
                    Browse the shop to add items.
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-xs" data-id="element-74" data-mp="1beotptu858cv">
                    <Link to="/shop" onClick={closeCart} className="px-6 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-75" data-mp="1bddnzoh7xqj4">
                      Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-76" data-mp="2vw6dj6j3bb6g" />
                    </Link>
                  </div>
                </div> : <ul className="bg-white" data-id="element-77" data-mp="39nckfnop8b3d">
                  {items.map(item => <li key={item.productId} className="px-4 py-4 border-b border-[#E5E5E5] last:border-b-0 flex gap-3" data-id="element-78" data-mp="35naugbsbt63x">
                      {/* Image */}
                      {getCartItemImage(item, products) && <div className="w-20 h-20 shrink-0 flex items-center justify-center" data-id="element-79" data-mp="2cyn0lrtih8er">
                          <img src={getCartItemImage(item, products)} alt="" className="max-w-full max-h-full object-contain" data-id="element-80" data-mp="2qy4s4ey6yzrt" />
                        </div>}

                      {/* Details */}
                      <div className="flex-1 min-w-0" data-id="element-81" data-mp="67xasnk8g3ir">
                        <p className="text-sm font-bold text-[#111111] leading-snug" data-id="element-82" data-mp="66ch105qsoly">
                          {item.name}
                        </p>

                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mt-0.5" data-id="element-83" data-mp="2v12ic640czha">
                          {item.type === 'book' ? 'Digital Book' : 'Supplement'}
                        </p>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-3" data-id="element-84" data-mp="3418v1prvtvfe">
                          <div className="flex items-center border border-[#E5E5E5] rounded-md overflow-hidden" data-id="element-85" data-mp="3913hnh4mjyun">
                            <button onClick={() => {
                      if (item.quantity === 1) {
                        removeItem(item.productId);
                      } else {
                        updateQuantity(item.productId, item.quantity - 1);
                      }
                    }} className={`px-2.5 py-1.5 transition-colors ${item.quantity === 1 ? 'text-[#5A5A5A] hover:bg-burgundy/10 hover:text-burgundy' : 'text-[#5A5A5A] hover:bg-[#F9F9F9]'}`} aria-label={item.quantity === 1 ? 'Remove item' : 'Decrease quantity'} data-id="element-86" data-mp="2cbcfqwu079et">
                              {item.quantity === 1 ? <Trash2 className="w-3 h-3" data-id="element-87" data-mp="1q1ngvcm1tgls" /> : <Minus className="w-3 h-3" data-id="element-88" data-mp="1q0ukn5kpu9hw" />}
                            </button>
                            <span className="px-3 text-sm font-bold text-[#111111] min-w-[1.75rem] text-center" data-id="element-89" data-mp="1l3ufcwvoxaru">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-2.5 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Increase quantity" data-id="element-90" data-mp="1c2wa6b9imx7o">
                              <Plus className="w-3 h-3" data-id="element-91" data-mp="12bjflbwc1sxy" />
                            </button>
                          </div>
                          <button type="button" onClick={() => removeItem(item.productId)} className="cart-delete-button text-sm font-medium text-[#2D2D2D] hover:text-burgundy transition-colors inline-flex items-center gap-1" data-id="element-92" data-mp="xdi0f2212drr">
                            <Trash2 className="w-3.5 h-3.5" data-id="element-93" data-mp="jdgzjmlrj6ca" />
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="shrink-0 text-right" data-id="element-94" data-mp="2c9hui9i3vz3v">
                        <p className="text-sm font-bold text-[#111111]" data-id="element-95" data-mp="2c6vmy9v7tjln">
                          {formatPrice(item.unitPrice * item.quantity)}
                        </p>
                      </div>
                    </li>)}
                </ul>}
            </div>

            {/* Footer */}
            {items.length > 0 && <div className="px-6 py-6 space-y-4 bg-[#FBFBFB]" data-id="element-96" data-mp="1tadozaprwngy">
                <div className="text-right" data-id="element-97" data-mp="1tboqf3vqxj4w">
                  <p className="text-base text-[#111111]" data-id="element-98" data-mp="1elr6j04unfli">
                    Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
                    <span className="font-bold" data-id="element-99" data-mp="1jl3tdkvq0mo3">{formatPrice(subtotal)}</span>
                  </p>
                </div>
                <p className="text-sm text-[#5A5A5A]" data-id="element-100" data-mp="15mw1vshgedkb">
                  Shipping and taxes calculated at checkout.
                </p>
                <button onClick={handleCheckout} className="w-full px-8 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3 text-[18px]" data-id="element-101" data-mp="qxkol99pwon9">
                  Checkout 
                </button>
              </div>}
          </motion.aside>
        </>}
    </AnimatePresence>;
}